/**
 * BillingPage — route /billing (alias /pricing).
 *
 * - Fetches live plans from GET /api/billing/plans.
 * - Falls back to two hard-coded plans if the API returns nothing.
 * - Fetches subscription status via GET /api/billing/subscription.
 * - Shows "Manage subscription" CTA when already subscribed.
 * - Shows per-plan "Start free trial" CTAs otherwise.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plan, SubscriptionStatus } from '../../types/api';
import { getPlans, getSubscription, startCheckout, openBillingPortal } from '../../api/billing';
import { isNativeIOS } from '../../utils/platform';
import FamilyMembers from './FamilyMembers';
import logoUrl from '../../assets/images/cleveroo-logo.png';
import '../../components/AccountPages.css';

// ─── Fallback plans shown when the API returns an empty list ─────────────────

const FALLBACK_PLANS: Plan[] = [
  {
    id: 'fallback_monthly',
    stripe_price_id: 'price_monthly',
    name: 'Monthly',
    billing_interval: 'month',
    amount_cents: 1499,
    currency: 'aud',
    trial_period_days: 14,
    features: [
      'One family subscription — covers all your children',
      'Unlimited practice tests',
      'Detailed progress reports',
      'All subjects & difficulty levels',
      'Adaptive learning path',
      'Parent progress dashboard',
    ],
  },
  {
    id: 'fallback_annual',
    stripe_price_id: 'price_annual',
    name: 'Annual',
    billing_interval: 'year',
    amount_cents: 14900,
    currency: 'aud',
    trial_period_days: 14,
    features: [
      'Everything in Monthly',
      'Save ~17% vs monthly billing',
      'Priority email support',
      'Printable progress certificates',
    ],
  },
];

// ─── Free vs Premium comparison ────────────────────────────────────────────────
// Mirrors the enforced boundary (ROO-82): free tier = placement + score report +
// 3 full practice tests + daily practice + games/streak; paid = unlimited tests,
// full reading/cloze sets, historical progress, parent dashboard.
interface ComparisonRow {
  label: string;
  free: string | boolean;
  premium: string | boolean;
}

const COMPARISON_ROWS: ComparisonRow[] = [
  { label: 'Placement quiz & score report', free: true, premium: true },
  { label: 'Full practice tests', free: '3 free', premium: 'Unlimited' },
  { label: 'Daily practice, games & streak', free: true, premium: true },
  { label: 'Full reading & cloze question sets', free: false, premium: true },
  { label: 'Historical progress reports', free: false, premium: true },
  { label: 'Parent dashboard', free: false, premium: true },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatPrice(amountCents: number, interval: 'month' | 'year'): string {
  const dollars = amountCents / 100;
  const formatted =
    dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`;
  return `${formatted}/${interval === 'month' ? 'mo' : 'yr'}`;
}

function trialLine(days: number): string {
  return days > 0 ? `${days}-day free trial` : 'No trial';
}

/** One cell in the free/premium comparison grid — a checkmark, a dash, or a short label. */
const ComparisonCell: React.FC<{ value: string | boolean; highlight?: boolean }> = ({ value, highlight }) => {
  if (value === true) {
    return (
      <svg
        width="17"
        height="17"
        viewBox="0 0 24 24"
        fill="none"
        stroke={highlight ? '#14593C' : '#6B7A6F'}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ display: 'block', margin: '0 auto' }}
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
    );
  }
  if (value === false) {
    return <span style={{ display: 'block', textAlign: 'center', color: '#C7BFA6', fontSize: 15 }}>—</span>;
  }
  return (
    <span
      style={{
        display: 'block',
        textAlign: 'center',
        fontSize: 13,
        fontWeight: 700,
        color: highlight ? '#14593C' : '#6B7A6F',
      }}
    >
      {value}
    </span>
  );
};

function intervalLabel(plan: Plan): string {
  if (plan.billing_interval === 'year') {
    // Show the per-month equivalent so the annual plan feels cheaper at a glance
    const perMonth = plan.amount_cents / 12 / 100;
    return `≈ $${perMonth.toFixed(2)}/mo — billed annually`;
  }
  return 'Billed monthly';
}

// ─── Component ───────────────────────────────────────────────────────────────

const BillingPage: React.FC = () => {
  const navigate = useNavigate();

  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [loadingSub, setLoadingSub] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    // Fetch plans
    try {
      const fetched = await getPlans();
      setPlans(fetched.length > 0 ? fetched : FALLBACK_PLANS);
    } catch {
      setPlans(FALLBACK_PLANS);
    } finally {
      setLoadingPlans(false);
    }

    // Fetch subscription (auth required — may fail for non-authed users)
    try {
      const sub = await getSubscription();
      setSubscription(sub);
    } catch {
      setSubscription(null);
    } finally {
      setLoadingSub(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCheckout = async (plan: Plan) => {
    setCheckoutLoading(plan.id);
    setError(null);
    try {
      await startCheckout(plan.stripe_price_id);
    } catch (err: any) {
      setError(err?.message || 'Could not start checkout. Please try again.');
      setCheckoutLoading(null);
    }
  };

  const handlePortal = async () => {
    setPortalLoading(true);
    setError(null);
    try {
      await openBillingPortal();
    } catch (err: any) {
      setError(err?.message || 'Could not open billing portal. Please try again.');
      setPortalLoading(false);
    }
  };

  const isSubscribed = !!subscription?.active;
  const isLoading = loadingPlans || loadingSub;

  const displayPlans = plans.length > 0 ? plans : FALLBACK_PLANS;

  return (
    <div className="cvr-account">
      {/* Top bar */}
      <div className="cvr-account__bar">
        <button
          type="button"
          className="cvr-account__back"
          onClick={() => navigate('/')}
        >
          ← Dashboard
        </button>
        <span className="cvr-account__brand">
          <img src={logoUrl} alt="" />
          Cleveroo
        </span>
      </div>

      <div className="cvr-account__wrap" style={{ maxWidth: 800 }}>
        {/* Page heading */}
        <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
          <h1 className="cvr-account__title" style={{ fontSize: 32 }}>
            Simple, honest pricing
          </h1>
          <p style={{ color: '#6B7A6F', fontSize: 15, marginTop: 8 }}>
            Start your free 14-day trial today. No credit card required. Cancel any time.
          </p>
        </div>

        {/* Headline feature — one subscription covers every child in the family */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            background: '#DCEFCC',
            border: '1.5px solid #14593C',
            borderRadius: 999,
            padding: '11px 20px',
            margin: '0 auto',
            maxWidth: 480,
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#14593C"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ flexShrink: 0 }}
          >
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#14593C' }}>
            One family subscription — covers all your children
          </span>
        </div>

        {/* Error banner */}
        {error && (
          <div
            style={{
              background: '#FAD5C8',
              border: '1.5px solid #E26B4F',
              borderRadius: 12,
              padding: '13px 18px',
              color: '#8B2E14',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {error}
          </div>
        )}

        {/* Already subscribed — manage panel */}
        {!isLoading && isSubscribed && (
          <div className="cvr-card" style={{ textAlign: 'center', padding: '32px 24px' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#14593C"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ display: 'inline-block' }}
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h2
              style={{
                fontFamily: "'Bricolage Grotesque', system-ui, sans-serif",
                fontWeight: 800,
                fontSize: 22,
                color: '#1A2A1F',
                marginBottom: 8,
              }}
            >
              You have an active subscription
            </h2>
            <p style={{ color: '#6B7A6F', fontSize: 15, marginBottom: 24 }}>
              Status:{' '}
              <strong style={{ color: '#14593C', textTransform: 'capitalize' }}>
                {subscription?.status ?? 'active'}
              </strong>
              {subscription?.cancelAtPeriodEnd && (
                <span style={{ color: '#E26B4F' }}>
                  {' '}· cancels at period end
                </span>
              )}
            </p>
            <button
              onClick={handlePortal}
              disabled={portalLoading}
              className="cvr-btn"
            >
              {portalLoading ? 'Opening portal…' : 'Manage subscription'}
            </button>
          </div>
        )}

        {/* Plan cards — shown when not subscribed or while loading */}
        {!isLoading && !isSubscribed && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 18,
            }}
          >
            {displayPlans.map((plan) => {
              const isAnnual = plan.billing_interval === 'year';
              const isThisLoading = checkoutLoading === plan.id;

              return (
                <div
                  key={plan.id}
                  className="cvr-card"
                  style={{
                    position: 'relative',
                    border: isAnnual
                      ? '2px solid #14593C'
                      : '1.5px solid #EFE6D0',
                    padding: '28px 24px',
                  }}
                >
                  {/* "Best value" badge for annual */}
                  {isAnnual && (
                    <span
                      style={{
                        position: 'absolute',
                        top: -13,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#14593C',
                        color: 'white',
                        fontSize: 11,
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        padding: '4px 14px',
                        borderRadius: 999,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Best value · ~17% off
                    </span>
                  )}

                  {/* Plan name */}
                  <p
                    style={{
                      fontFamily: "'Bricolage Grotesque', system-ui, sans-serif",
                      fontWeight: 800,
                      fontSize: 20,
                      color: '#1A2A1F',
                      marginBottom: 4,
                    }}
                  >
                    {plan.name}
                  </p>

                  {/* Price */}
                  <div style={{ marginBottom: 6 }}>
                    <span
                      style={{
                        fontFamily: "'Bricolage Grotesque', system-ui, sans-serif",
                        fontWeight: 800,
                        fontSize: 36,
                        color: '#14593C',
                      }}
                    >
                      {formatPrice(plan.amount_cents, plan.billing_interval)}
                    </span>
                    <span
                      style={{ fontSize: 12, color: '#6B7A6F', marginLeft: 6 }}
                    >
                      AUD incl. GST
                    </span>
                  </div>

                  <p style={{ fontSize: 13, color: '#6B7A6F', marginBottom: 4 }}>
                    {intervalLabel(plan)}
                  </p>
                  <p
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: '#F58720',
                      marginBottom: 20,
                    }}
                  >
                    {trialLine(plan.trial_period_days ?? 14)} included
                  </p>

                  {/* Features */}
                  <ul
                    style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: '0 0 24px',
                      display: 'grid',
                      gap: 10,
                    }}
                  >
                    {(plan.features || []).map((feat, i) => (
                      <li
                        key={i}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 9,
                          fontSize: 14,
                          color: '#1A2A1F',
                        }}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#14593C"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ flexShrink: 0, marginTop: 2 }}
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        {feat}
                      </li>
                    ))}
                  </ul>

                  {/* CTA — hidden in the iOS shell (App Store 3.1.1: no
                      external purchases in-app; reader-app model, ROO-235) */}
                  {isNativeIOS() ? (
                    <p
                      style={{
                        fontSize: 13,
                        color: '#6B7A6F',
                        textAlign: 'center',
                        margin: 0,
                        padding: '12px 0',
                      }}
                    >
                      Subscription management isn't available in the app.
                    </p>
                  ) : (
                  <button
                    className="cvr-btn"
                    onClick={() => handleCheckout(plan)}
                    disabled={isThisLoading || !!checkoutLoading}
                    style={{
                      width: '100%',
                      justifyContent: 'center',
                      background: isAnnual ? '#14593C' : 'white',
                      color: isAnnual ? 'white' : '#1A2A1F',
                      borderColor: isAnnual ? '#14593C' : '#E5D9BD',
                    }}
                  >
                    {isThisLoading
                      ? 'Redirecting…'
                      : `Start ${plan.trial_period_days ?? 14}-day free trial`}
                  </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Skeleton while loading */}
        {isLoading && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 18,
            }}
          >
            {[0, 1].map((i) => (
              <div
                key={i}
                className="cvr-card"
                style={{ height: 340, opacity: 0.4 }}
              />
            ))}
          </div>
        )}

        {/* Free vs Premium comparison */}
        <div className="cvr-card" style={{ padding: '22px 24px' }}>
          <h2
            style={{
              fontFamily: "'Bricolage Grotesque', system-ui, sans-serif",
              fontWeight: 800,
              fontSize: 18,
              color: '#1A2A1F',
              margin: '0 0 14px',
              textAlign: 'center',
            }}
          >
            Free vs Premium
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', rowGap: 10, columnGap: 16 }}>
            <span />
            <span style={{ fontSize: 12, fontWeight: 800, color: '#6B7A6F', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Free
            </span>
            <span style={{ fontSize: 12, fontWeight: 800, color: '#14593C', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Premium
            </span>
            {COMPARISON_ROWS.map((row) => (
              <React.Fragment key={row.label}>
                <span style={{ fontSize: 14, color: '#1A2A1F' }}>{row.label}</span>
                <ComparisonCell value={row.free} />
                <ComparisonCell value={row.premium} highlight />
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Legal footer */}
        <p
          style={{
            textAlign: 'center',
            fontSize: 12,
            color: '#6B7A6F',
            lineHeight: 1.7,
          }}
        >
          Prices shown in AUD and include GST. You will not be charged until your
          free trial ends. Cancel any time from the billing portal.
        </p>

        {/* Family / manage children */}
        <div>
          <h2
            style={{
              fontFamily: "'Bricolage Grotesque', system-ui, sans-serif",
              fontWeight: 800,
              fontSize: 20,
              color: '#1A2A1F',
              marginBottom: 4,
            }}
          >
            Family
          </h2>
          <p style={{ color: '#6B7A6F', fontSize: 14, marginBottom: 14 }}>
            Link your children's accounts so they share your subscription.
          </p>
          <FamilyMembers />
        </div>
      </div>
    </div>
  );
};

export default BillingPage;
